/**
 * Enum types for the Covered Calls Tracker.
 */

/** Type of trade entry. */
export enum TradeType {
  BUY_WRITE = 'BUY_WRITE',
  SEPARATE = 'SEPARATE',
  PROTECTIVE_PUT = 'PROTECTIVE_PUT',
}

/** Lifecycle status of a long protective put. Mirrors OptionStatus values. */
export enum PutStatus {
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  CLOSED = 'CLOSED',
  EXERCISED = 'EXERCISED',
}

/** Recorded end-of-life result of a protective put in put history. */
export enum PutOutcome {
  EXPIRED = 'EXPIRED',
  CLOSED = 'CLOSED',
  EXERCISED = 'EXERCISED',
}

/** Current status of a trade position. */
export enum PositionStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}

/** Current status of an option contract. */
export enum OptionStatus {
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  CLOSED = 'CLOSED',
  ASSIGNED = 'ASSIGNED',
}

/** Outcome of a completed option contract in option history. */
export enum OptionOutcome {
  EXPIRED = 'EXPIRED',
  CLOSED = 'CLOSED',
  ASSIGNED = 'ASSIGNED',
  ROLLED = 'ROLLED',
}

/** Type of rolling strategy applied to an option. */
export enum RollType {
  UP = 'UP',
  DOWN = 'DOWN',
  OUT = 'OUT',
  UP_AND_OUT = 'UP_AND_OUT',
}

/** The exchange an underlying trades on. Kept small and extensible. */
export enum Exchange {
  ASX = 'ASX',
  US = 'US',
}

/** The instrument class of a trade leg. */
export enum InstrumentClass {
  SHARES = 'SHARES',
  OPTIONS = 'OPTIONS',
}

/**
 * A tenant's theme selection.
 * Shared by backend persistence and the frontend theme control.
 * `System` follows the OS `prefers-color-scheme`.
 */
export type ThemePreference = 'Light' | 'Dark' | 'System';

/**
 * Lifecycle state of a tenant's marketing-onboarding flow.
 * Absent/legacy items default to `NOT_STARTED` (Req 1.6); `COMPLETED` and
 * `DISMISSED` are terminal states that never revert.
 */
export type OnboardingState = 'NOT_STARTED' | 'COMPLETED' | 'DISMISSED';

/**
 * Where a marketing-consent decision was made (Req 4.3, 4.4).
 * `ONBOARDING` = captured during the post-sign-in onboarding prompt;
 * `SETTINGS` = changed later from the Profile section in Settings.
 */
export type ConsentSource = 'ONBOARDING' | 'SETTINGS';

/** Fixed choice set for the "How did you hear about us?" segmentation field (Req 2.3, 2.4). */
export type HearAboutUsChoice =
  | 'SEARCH'
  | 'SOCIAL_MEDIA'
  | 'FRIEND'
  | 'PODCAST'
  | 'OTHER';

/** Fixed choice set for the investing-experience segmentation field (Req 2.3). No "Other". */
export type InvestingExperienceChoice =
  | 'BEGINNER'
  | 'INTERMEDIATE'
  | 'ADVANCED';

/** Fixed choice set for the primary-goal segmentation field (Req 2.3, 2.4). */
export type PrimaryGoalChoice =
  | 'INCOME'
  | 'GROWTH'
  | 'HEDGING'
  | 'LEARNING'
  | 'OTHER';

/**
 * App-carried privacy/marketing policy version stamped into every ConsentRecord
 * (Decision 6, Req 4.1). Bump this when the consent policy text changes.
 */
export const POLICY_VERSION = '2024-06-01';
